// @flow

import * as React from 'react';

import isEmpty from 'lodash/isEmpty';
import { CornerDownRight } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { selectPricingInfos } from '@gandi/billing';
import {
  selectOneProductErrors,
  getCart,
  removeCartProduct,
  useDiscountDetails,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { selectMailboxesPrices } from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Fqdn } from '@gandi/ui';

import { fetchMailboxesPrices } from 'src/MailboxCreate/modules/actions';

import { useAppSelector } from 'src/hooks';
import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import type { FullState } from 'src/types';

import { ListRow } from '../../ListRow';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItemBase from './components/ProductItemBase';
import { TitleRow, TotalRow } from './components/Rows';
import type { TitleRowProps } from './components/Rows';
import { DiscountRow } from './components/Rows/DiscountRow';

const selectCurrentPrices = (state: FullState) => {
  const { sharingId, currency, grid } = selectPricingInfos(state);
  return selectMailboxesPrices(state, {
    process: 'create',
    productType: 'mailboxv2',
    sharingId,
    currency,
    grid,
  });
};

type Props = {
  product: DecoratedProduct,
};

export function MailboxUpdate({ product }: Props): React.MixedElement {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();
  const { __, formatDate } = useTranslator();
  const dispatch = useDispatch();

  const discountDetails = useDiscountDetails({ product });

  const prices = useAppSelector(selectCurrentPrices);

  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  // compoentDidMount
  React.useEffect(() => {
    if (isEmpty(prices)) {
      dispatch(fetchMailboxesPrices());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Remove all mailboxes of a defined productType of the cart.
   */
  const removeFromCart: TitleRowProps['handleRemoveAction'] = (
    e: SyntheticEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();

    if (product.quantity > 0) {
      dispatch(removeCartProduct(product.id))
        .then(() => dispatch(getCart()))
        .catch(() => {
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          });
        });
    }
  };

  const label = <>{__('Upgrade to 50 GB')}</>;

  if (productErrors.length > 0) {
    return <ProductErrorItem label={label} errors={productErrors} />;
  }

  const detail = <Fqdn>{product.payload.fqdn || ''}</Fqdn>;

  const info = __('Expires: %(dateEnd)s', {
    dateEnd: formatDate(product.expirationDate || '', 'LL'),
  });
  return (
    <ProductItemBase
      header={
        <TitleRow
          label={label}
          detail={detail}
          productType={product.productType}
          info={info}
          handleRemoveAction={removeFromCart}
        />
      }
      footer={
        <TotalRow
          totalPrice={
            product.prices.length
              ? product.computedTotalPrices.beforeTaxes
              : undefined
          }
          priceCrossedOut={product.computedTotalPrices.beforeDiscount}
          hasTaxes={product.hasTaxes}
        />
      }
    >
      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${product.id}-${discount.label}`}
          label={discount.label}
          // $FlowFixMe[incompatible-type] - price can be a string
          price={discount.price}
        />
      ))}
      {FF_CART_OVERVIEW_NDS && (
        <ListRow
          intent="muted"
          icon={<CornerDownRight size={16} strokeWidth={1.25} />}
          title={detail}
          options={[info]}
          optionsArrow={false}
        />
      )}
    </ProductItemBase>
  );
}
